<template>
    <v-row class="white-card" dense align="center">
        <v-col cols="12" md="4" v-if="isCompany">
            <kimsa-combobox :label="$t('statistics.select_coach')"
                :placeholder="$t('statistics.select_coach_placeholder')" outlined dense solo :items="coaches"
                @change="currentCoach = $event" :value="currentCoach" placeholder-class icon="$account"
                item-value="objectID" />
        </v-col>
        <v-col cols="12" md="4" v-if="!isStudent">
            <kimsa-combobox :label="$t('statistics.select_student')"
                :placeholder="$t('statistics.select_student_placeholder')" outlined dense solo :items="students"
                @change="currentStudent = $event" :value="currentStudent" placeholder-class icon="$account"
                item-value="objectID" />
        </v-col>
        <v-col cols="6" md="2">
            <kimsa-date-picker :label="$t('statistics.from')" solo flat dense outlined color="secondary" :max="endDate"
                @change="startDate = $event" :value="startDate" append-icon="$calendar" ref="start" class="mb-2" />
        </v-col>
        <v-col cols="6" md="2">
            <kimsa-date-picker :disabled="!startDate" :label="$t('statistics.to')" solo flat dense outlined
                color="secondary" :min="startDate" @change="endDate = $event" :value="endDate" append-icon="$calendar"
                ref="end" class="mb-2" />
        </v-col>
        <v-col cols="9" md="3">
            <v-btn class="b-primary mt-3" block depressed :loading="loading" @click="loadDocs" :disabled="disabled">
                {{ $t('statistics.load') }}
            </v-btn>
        </v-col>
        <v-col cols="3" md="1">
            <v-btn class="b-primary mt-3" block depressed :loading="loadingExport" @click="exportDocs"
                :disabled="disabled || docs.length === 0">
                <v-icon>mdi-file-export-outline</v-icon>
            </v-btn>
        </v-col>
    </v-row>
</template>

<script>
import { Client, UserIndex } from "@/utils/Algolia";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import KimsaDatePicker from "@/components/common/inputs/KimsaDatePicker";
import { db } from "@/utils/Firebase";
import XLSX from "xlsx";
import { getMergedBlocks } from "../../utils/Exercises";

export default {
    name: "StatisticsForm",
    components: { KimsaDatePicker, KimsaCombobox },
    data() {
        return {
            loading: false,
            students: [],
            coaches: [],
            currentStudent: null,
            currentCoach: null,
            startDate: null,
            endDate: null,
            docs: [],
            loadingExport: false,
            rpeTable: [
                { name: 10, reps: [100, 95, 91, 87, 85, 83, 81, 79] },
                { name: 9.5, reps: [97, 93, 89, 86, 84, 82, 80, 77.5] },
                { name: 9, reps: [95, 91, 87, 85, 83, 81, 79, 76] },
                { name: 8.5, reps: [93, 89, 86, 84, 82, 80, 77.5, 74.5] },
                { name: 8, reps: [91, 87, 85, 83, 81, 79, 76, 73] },
                { name: 7.5, reps: [89, 86, 84, 82, 80, 77.5, 74.5, 71.5] },
                { name: 7, reps: [87, 85, 83, 81, 79, 76, 73, 70] },
            ],
            rirTable: [
                { name: 0, reps: [100, 95, 91, 87, 85, 83, 81, 79] },
                { name: 1, reps: [95, 91, 87, 85, 83, 81, 79, 76] },
                { name: 2, reps: [91, 87, 85, 83, 81, 79, 76, 73] },
                { name: 3, reps: [89, 86, 84, 82, 80, 77.5, 74.5, 71.5] },
                { name: 4, reps: [87, 85, 83, 81, 79, 76, 73, 70] },
            ],
        }
    },
    computed: {
        coachId(vm = this) {
            return vm.isCompany ?
                vm.currentCoach?.objectID :
                vm.currentUser?.uid
        },
        isCompany(vm = this) {
            return vm.account?.accountType === 'company'
        },
        isStudent(vm = this) {
            return vm.account?.accountType === 'student'
        },
        studentId(vm = this) {
            return vm.isStudent ?
                vm.currentUser?.uid :
                vm.currentStudent?.objectID
        },
        studentName(vm = this) {
            return vm.isStudent ?
                vm.account?.name :
                vm.currentStudent?.name
        },
        disabled(vm = this) {
            return (!vm.currentStudent && !vm.isStudent) || !vm.startDate || !vm.endDate
        },
    },
    async mounted() {
        const vm = this
        // console.log('vm', vm)

        vm.startDate = vm.$moment().startOf('month').format('YYYY-MM-DD')
        vm.endDate = vm.$moment().format('YYYY-MM-DD')

        if (vm.isCompany) await vm.loadCoaches()
        if (!vm.isStudent) await vm.loadStudents()
    },
    methods: {
        async loadCoaches() {
            const vm = this
            let filter = `coachSubscription.companyId: ${vm.currentUser.uid}`

            await Client.clearCache()
            await UserIndex.search('', {
                filters: filter,
                hitsPerPage: 10000,
            })
                .then(({ hits }) => {
                    vm.coaches = hits.map(hit => ({
                        ...hit,
                        name: hit.name || hit.email
                    }))
                })
                .catch(err => {
                    console.log(err);
                });
        },
        async loadStudents() {
            const vm = this
            let filter = `studentSubscription.coachId: ${vm.coachId} AND studentSubscription.paused != 1`

            await Client.clearCache()
            await UserIndex.search('', {
                filters: filter,
                hitsPerPage: 10000,
            })
                .then(({ hits }) => {
                    vm.students = hits
                })
                .catch(err => {
                    console.log(err);
                });
        },
        async loadDocs() {
            const vm = this
            vm.loading = true

            let routines = null
            vm.docs = []

            if (vm.startDate === vm.endDate) {
                routines = await db.collection('assignedRoutines')
                    .where('studentId', '==', vm.studentId)
                    .where('rawDate.full', "==", vm.startDate).get()
            } else {
                routines = await db.collection('assignedRoutines')
                    .where('studentId', '==', vm.studentId)
                    .where('rawDate.full', ">=", vm.startDate)
                    .where('rawDate.full', "<=", vm.endDate)
                    .orderBy('rawDate.full', 'asc').get()
            }

            let docs = await Promise.all(
                routines.docs.map(async doc => {
                    const blocks = await getMergedBlocks(doc.data().blocks || []);
                    return { ...doc.data(), id: doc.id, blocks: blocks };
                })
            );

            vm.docs = docs

            vm.$emit('load', vm.docs)
            vm.loading = false
        },
        exportDocs() {
            const vm = this
            vm.loadingExport = true
            const workbook = XLSX.utils.book_new()
            let statistics = XLSX.utils.json_to_sheet(vm.statisticsSheet())
            XLSX.utils.book_append_sheet(workbook, statistics, vm.$t('common.statistics'))
            const filename = vm.studentName ? `${vm.$t('common.statistics')} - ${vm.studentName}` : vm.$t('common.statistics')
            XLSX.writeFile(workbook, `${filename}.xlsx`)
            vm.loadingExport = false
        },
        statisticsSheet() {
            const vm = this
            return vm.docs.map(doc => {
                const preInfo = doc?.advance?.preInfo || null
                const wellness = Number((preInfo?.muscles_hurt + preInfo?.sleep_night + preInfo?.stressed + preInfo?.tired) / 4).toFixed(0)
                const postInfo = doc?.advance?.postInfo || null
                const duration = Number(postInfo?.duration || 0)
                const effort = Number(postInfo?.effort || 0)
                const charge = duration * effort
                const minutes = doc.type === 'competence' ? duration : 0

                let sets_total = 0
                let sets_strength = 0
                let sets_speed = 0
                let sets_resistance = 0
                let percentage = 0
                let rpe = 0
                let rir = 0
                let tonnage = 0
                let distance = 0
                let quantity_strong = 0
                doc.blocks?.forEach(block => {
                    block.exercises.forEach(exercise => {
                        if (exercise.type !== 'rest') {
                            let sets = Number(exercise.config?.sets?.length || 0)
                            sets_total += sets
                            if (exercise.categories?.[0] === vm.$t('objectives.strong')) sets_strength += sets
                            if (exercise.categories?.[0] === vm.$t('objectives.speed')) sets_speed += sets
                            if (exercise.categories?.[0] === vm.$t('objectives.resistance')) sets_resistance += sets

                            exercise.config?.sets?.forEach((set, i) => {
                                if (exercise.categories?.[0] === vm.$t('objectives.strong')) {
                                    let reps = vm.takeValueFromSet(set, 'reps') || 0
                                    tonnage += (vm.takeValueFromSet(set, 'weight') * reps)
                                    let valuePercentage = vm.takeValueFromSet(set, 'percentage')
                                    percentage += valuePercentage
                                    let valueRpe = vm.takeValueFromSet(set, 'rpe')
                                    rpe += valueRpe ? vm.getValueFromTable(valueRpe, set, 'rpeTable') : 0
                                    let valueRir = vm.takeValueFromSet(set, 'rir')
                                    rir += valueRir ? vm.getValueFromTable(valueRir, set, 'rirTable') : 0
                                    if (valuePercentage > 0 || valueRir > 0 || valueRpe > 0) quantity_strong++
                                } else if (exercise.categories?.[0] === vm.$t('objectives.speed') || exercise.categories?.[0] === vm.$t('objectives.resistance')) {
                                    distance += (vm.takeValueFromSet(set, 'distance') * sets)
                                }
                            })
                        }
                    })
                })

                let intensity_strength = percentage + rpe + rir
                if (quantity_strong > 0) intensity_strength /= quantity_strong

                return {
                    [vm.$t('common.date')]: doc.rawDate.full,
                    [vm.$t('statistics.wellness')]: Number(wellness === 'NaN' ? 0 : wellness || 0),
                    [vm.$t('common.duration')]: duration,
                    [vm.$t('statistics.effort')]: effort,
                    [vm.$t('statistics.charge')]: charge,
                    [vm.$t('statistics.sets_total')]: sets_total,
                    [vm.$t('statistics.sets_strength')]: sets_strength,
                    [vm.$t('statistics.sets_speed')]: sets_speed,
                    [vm.$t('statistics.sets_resistance')]: sets_resistance,
                    [vm.$t('statistics.intensity_strength')]: Number(intensity_strength).toFixed(1).replace('.0', ''),
                    [vm.$t('statistics.tonnage')]: tonnage,
                    [vm.$t('statistics.distance')]: distance,
                    [vm.$t('statistics.minutes')]: minutes,
                }
            })
        },
        reset() {
            const vm = this
            if (vm.disabled) vm.$emit('reset')
        },
        takeValueFromSet(set, attribute) {
            const vm = this
            let defaultValue = 0
            let value = set.data.find(data => data.name === vm.$t(`exercises.${attribute}`))
            return !!value || value === 0 ? Number(value.value || 0) : defaultValue
        },
        getValueFromTable(value, set, typeTable) {
            const vm = this
            if (typeTable === 'rpeTable') {
                if (value > 10) value = 10
                if (value < 7) value = 7
            }
            if (typeTable === 'rirTable') {
                if (value > 4) value = 4
                if (value < 0) value = 0
            }
            let findValueTable = vm[typeTable]?.find(table => table.name == value) || null
            let reps = set.data.find(data => data.name === vm.$t('exercises.reps')) || null
            let repsValue = reps && (Number(reps.value || 0) - 1) || 0
            if (repsValue > 7) repsValue = 7
            if (repsValue < 0) repsValue = 0
            return findValueTable && repsValue && findValueTable.reps[repsValue] || 0
        },
    },
    watch: {
        currentCoach(val) {
            this.reset()
            if (val?.objectID) this.loadStudents()
        },
        currentStudent() {
            this.reset()
        },
        startDate(val) {
            this.reset()
            this.$emit('change:date:start', val)
        },
        endDate() {
            this.reset()
        },
        studentId(val) {
            this.$emit('change:student', val)
        },
        coachId(val) {
            this.$emit('change:coach', val)
        }
    }
}
</script>

<style scoped></style>